import {
    Alert,
    Button,
    Card,
    Space,
    Table,
    Modal,
    Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import { FaRegTrashAlt } from 'react-icons/fa';
import { BsArrowClockwise } from 'react-icons/bs';
import { BookingType } from '../../types/booking';
import { getBookingConcepts } from '../../api/booking';
import { revertAdjustmentTransaction } from '../../api/invoice-transactions';
import { formatCurrency } from '../../utils/numberFormatter';
import AdjustmentConcept from './AdjustmentConcept';
import { FINISHED_STATUSES } from '../../const/booking';

interface ConceptsProps {
    booking: BookingType;
}

interface IConcept {
    amount: number;
    type: string;
}

interface ITransactionConcept {
    _id: string;
    concepts: IConcept[];
    transactionID: string;
    total: number;
}

const { Text } = Typography;

const Concepts: React.FC<ConceptsProps> = ({ booking }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [concept, setConcept] = useState<ITransactionConcept | null>(null);
    const [error, setError] = useState<string>();
    const [addAdjustmentModal, setAddAdjustmentModal] = useState<boolean>(false);

    const fetchConcepts = () => {
        setLoading(true);
        getBookingConcepts(booking._id)
            .then(({ data }: any) => {
                setConcept(data.data);
            })
            .catch((err) => {
                const description = err.response.data.description || 'Error';
                setError(description);
            })
            .finally(() => setLoading(false));
    };

    const handleRevertAdjustment = () => {
        Modal.confirm({
            title: 'Revert Adjustment',
            content: 'Are you sure you want to revert the adjustment?',
            centered: true,
            onOk: () => {
                setLoading(true);
                revertAdjustmentTransaction(concept?.transactionID as string)
                    .then(() => fetchConcepts())
                    .catch((err: any) => {
                        const description = err.response.data.description || 'Error';
                        setError(description);
                    })
                    .finally(() => setLoading(false));
            },
        });
    };

    const columns = [
        {
            title: 'Concept',
            dataIndex: 'type',
            key: 'type',
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount: number) => formatCurrency(amount),
        },
        {
            title: 'Actions',
            key: 'amount',
            align: 'center' as 'center',
            width: 60,
            render: (_: any, record: IConcept) => record.type === 'Ajuste' && (
                <Button
                    key="adjustment"
                    type="link"
                    onClick={() => handleRevertAdjustment()}
                    icon={<FaRegTrashAlt style={{ verticalAlign: 'middle' }} color="red" />}
                />
            ),
        },
    ];

    useEffect(() => {
        if (!booking?._id || (!FINISHED_STATUSES.includes(booking.statusText))) return;
        fetchConcepts();
    }, [booking]);

    return (
        <Card
            style={{ height: '100%', marginBottom: 0 }}
            title="Concepts"
            extra={[
                <Space size={12}>
                    <Button
                        key="refresh"
                        onClick={() => fetchConcepts()}
                        type="ghost"
                        size="small"
                        icon={<BsArrowClockwise style={{ verticalAlign: 'middle' }} />}
                    />
                    <Button
                        key="add-adjustment"
                        type="primary"
                        size="small"
                        onClick={() => setAddAdjustmentModal(true)}
                    >
                        Adjustment
                    </Button>
                </Space>,
            ]}
        >
            {
                addAdjustmentModal && (
                    <AdjustmentConcept
                        transactionID={concept?.transactionID as string}
                        currency={booking.currency}
                        visible={addAdjustmentModal}
                        onCancel={() => setAddAdjustmentModal(false)}
                        fetchConcepts={fetchConcepts}
                        total={concept?.total || 0}
                    />
                )
            }
            {
                error && (
                    <Alert message={error} type="error" style={{ marginBottom: 12 }} />
                )
            }
            {
                !FINISHED_STATUSES.includes(booking.statusText) && (
                    <Alert
                        message="Concepts are available after the booking is finished."
                        type="info"
                        style={{ marginBottom: 12 }}
                    />
                )
            }
            <Table
                loading={loading}
                columns={columns}
                dataSource={concept?.concepts}
                rowKey="_id"
                size="small"
                summary={() => (
                    <Table.Summary fixed>
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={1}>
                                Total
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2}>
                                <Text
                                    strong
                                    type={!concept?.total ? 'secondary' : (concept?.total || 0) < 0 ? 'danger' : 'success'}
                                >
                                    {formatCurrency(concept?.total || 0)}
                                </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={3} />
                        </Table.Summary.Row>
                    </Table.Summary>
                )}
                pagination={false}
            />
        </Card>
    );
};

export { Concepts };
